/* eslint-disable factorial/no-raw-html */
import { useState } from 'react'

import { Box, colors } from '@factorialco/deprecated-design-system'
import { ResponsiveLine } from '@nivo/line'
import { sortBy } from 'lodash'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import EmptyState from 'modules/project_management/components/graphs/EmptyState'
import { overviewFrequencyOptionsEnum } from 'modules/project_management/lib/constants'
import { formatYAxisWithLargeNumbers } from 'modules/project_management/lib/formatChartComponents'
import { dynamicFiltering } from 'modules/project_management/lib/overviewOptions'

import EvolutionGraphLoadingSkeleton from '../../graphs/EvolutionGraph/EvolutionGraphLoadingSkeleton'
import { BreakdownTransformedData } from '../EvolutionChart/BreakdownEvolutionChart/getCostBreakdownTransformedData'
import Legend, { LegendItem } from '../Legend'
import { ResponsiveChartProps } from '../types/responsiveChartProps'

interface ResponsiveBreakdownChartProps {
  breakdownData: BreakdownTransformedData[]
  unit: string
  selectedFrequency: overviewFrequencyOptionsEnum
  onChangeBreakdownDataShown?: (activeSeries: string[]) => void
}

export const ResponsiveBreakdownChart: React.FC<
  ResponsiveChartProps<ResponsiveBreakdownChartProps>
> = (props) => {
  const { breakdownData, unit, selectedFrequency, onChangeBreakdownDataShown = undefined } = props

  const chartIds = breakdownData.map<string>((e) => e.id)
  const legendItems = breakdownData.map<LegendItem>((e) => ({
    id: e.id,
    label: e.label,
    color: e.color,
  }))

  const [activeSeries, setActiveSeries] = useState<string[]>(chartIds)

  const manageChartVisibility = (id: string) => {
    if (activeSeries.length === 1 && activeSeries.includes(id)) return
    let newSeries: string[]

    if (activeSeries.includes(id)) {
      newSeries = activeSeries.filter((i) => i !== id)
      setActiveSeries(newSeries)
    } else {
      newSeries = [...activeSeries, id]
      setActiveSeries(newSeries)
    }
    onChangeBreakdownDataShown && onChangeBreakdownDataShown(newSeries)
  }

  const filteredBreakdownData = sortBy(
    breakdownData.filter((evolution) => activeSeries.includes(evolution.id)),
    ['min', 'max']
  )

  const minValue = Math.min(...filteredBreakdownData.flatMap((e) => e.min))
  const maxValue = Math.max(...filteredBreakdownData.flatMap((e) => e.max))

  const flatData = minValue === maxValue

  const minY = () => (flatData ? 0 : 'auto')
  const maxY = () => (maxValue == 0 ? 4 : 'auto')

  const shownColors = filteredBreakdownData.map((d) => d.color)

  const _loading = 'loading' in props && props.loading
  const _error = 'error' in props && props.error

  useUnsafeMemo(() => {
    filteredBreakdownData.forEach((fbd) => {
      dynamicFiltering(fbd, selectedFrequency)
    })
  }, [filteredBreakdownData, selectedFrequency])

  return (
    <>
      <Box height="s240" width="full">
        {!_loading && _error && (
          <Box height="s300" width="full">
            <EmptyState
              title={i18n.t('project_management.graphs.missing_data.title')}
              description={i18n.t('project_management.graphs.missing_data.description')}
              icon="monitor-flash"
            />
          </Box>
        )}
        {_loading && <EvolutionGraphLoadingSkeleton />}
        <ResponsiveLine
          data={filteredBreakdownData}
          enableArea
          areaBaselineValue={minValue}
          areaOpacity={0.25}
          areaBlendMode={'normal'}
          margin={{ left: 68, top: 10, bottom: 34, right: 50 }}
          xScale={{ type: 'linear' }}
          yScale={{
            type: 'linear',
            min: minY(),
            max: maxY(),
            stacked: true,
            reverse: false,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 14,
            tickRotation: 0,
            tickValues: 4,
            format: (value) => formatYAxisWithLargeNumbers(value, unit),
          }}
          enableGridY
          gridYValues={4}
          yFormat=" >-.2f"
          colors={shownColors.map((c) => colors[c])}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickRotation: 0,
            tickPadding: 14,
            tickValues: filteredBreakdownData[0]?.data.length || 7,
            renderTick: ({ textAnchor, textX, textY, value, x, y }) => {
              return (
                <g transform={`translate(${x},${y + 8})`}>
                  <text textAnchor={textAnchor} transform={`translate(${textX},${textY + 10})`}>
                    <tspan>{filteredBreakdownData[0]?.data[value]?.title}</tspan>
                  </text>
                </g>
              )
            },
          }}
          pointSize={7}
          pointBorderWidth={2}
          pointLabelYOffset={-12}
          useMesh
          enableGridX={false}
          enableSlices="x"
          pointSymbol={(point) => (
            <circle
              cx="0"
              cy="0"
              r={point.datum.x !== 0 ? '3' : '0'}
              stroke={point.color}
              strokeWidth="2"
              fill={colors.grey000White}
            />
          )}
          theme={{
            grid: {
              line: {
                stroke: '#dddddd',
                strokeWidth: 1,
                strokeDasharray: '4 4',
              },
            },
          }}
          animate={false}
          markers={[]}
        />
      </Box>
      <Legend
        items={legendItems}
        legendAction={manageChartVisibility}
        activeLegends={activeSeries}
      />
    </>
  )
}
